'use client';
import React, { useState } from 'react';
import { User, Mail, Phone, MapPin, Edit2, Save, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';


export default function Profile() {
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(false);

  const defaultName = user?.user_metadata?.full_name || user?.email?.split('@')?.[0] || 'Devotee';
  const [form, setForm] = useState({
    name: defaultName,
    email: user?.email || '',
    phone: user?.user_metadata?.phone || '',
    city: 'Bengaluru',
    state: 'Karnataka',
    language: 'Hindi',
    gotra: '',
    nakshatra: '',
  });

  const handleSave = () => {
    setSaved(true);
    setEditing(false);
    setTimeout(() => setSaved(false), 3000);
  };

  const initials = form.name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="space-y-5 max-w-2xl">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl font-bold text-maroon">Profile</h2>
          <p className="text-sm text-maroon/50 mt-0.5">Manage your personal information</p>
        </div>
        {!editing ? (
          <button
            onClick={() => setEditing(true)}
            className="flex items-center gap-1.5 text-sm font-semibold text-saffron border border-saffron/30 px-4 py-2 rounded-full hover:bg-saffron/5 transition-colors"
          >
            <Edit2 size={14} /> Edit
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={() => setEditing(false)}
              className="flex items-center gap-1.5 text-sm font-medium text-maroon/50 border border-gold/20 px-3 py-2 rounded-full hover:bg-cream transition-colors"
            >
              <X size={14} /> Cancel
            </button>
            <button
              onClick={handleSave}
              className="flex items-center gap-1.5 text-sm font-semibold text-white bg-saffron px-4 py-2 rounded-full hover:bg-saffron/90 transition-colors"
            >
              <Save size={14} /> Save
            </button>
          </div>
        )}
      </div>

      {saved && (
        <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm text-green-700 font-medium">
          ✓ Profile updated successfully
        </div>
      )}

      {/* Avatar */}
      <div className="bg-white rounded-2xl border border-gold/15 p-5 flex items-center gap-4">
        <div className="w-16 h-16 rounded-full saffron-gradient flex items-center justify-center text-white text-xl font-bold shadow-md flex-shrink-0">
          {initials}
        </div>
        <div>
          <h3 className="font-semibold text-maroon text-base">{form.name}</h3>
          <p className="text-sm text-maroon/50">{form.email}</p>
          <span className="text-xs bg-green-50 text-green-600 font-medium px-2 py-0.5 rounded-full mt-1 inline-block">✓ Verified</span>
        </div>
      </div>

      {/* Personal Info */}
      <div className="bg-white rounded-2xl border border-gold/15 overflow-hidden">
        <div className="px-5 py-4 border-b border-gold/10">
          <h3 className="text-sm font-semibold text-maroon flex items-center gap-2"><User size={15} className="text-saffron" /> Personal Information</h3>
        </div>
        <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { label: 'Full Name', key: 'name', icon: User },
            { label: 'Email', key: 'email', icon: Mail, disabled: true },
            { label: 'Phone', key: 'phone', icon: Phone },
            { label: 'City', key: 'city', icon: MapPin },
            { label: 'State', key: 'state', icon: MapPin },
            { label: 'Preferred Language', key: 'language', icon: null },
          ].map(({ label, key, icon: Icon, disabled }) => (
            <div key={key}>
              <label className="text-xs font-medium text-maroon/50 block mb-1">{label}</label>
              {editing && !disabled ? (
                <input
                  type="text"
                  value={form[key as keyof typeof form]}
                  onChange={e => setForm(prev => ({ ...prev, [key]: e.target.value }))}
                  className="w-full text-sm text-maroon border border-gold/20 rounded-xl px-3 py-2 outline-none focus:border-saffron/40 bg-cream/30"
                />
              ) : (
                <div className="flex items-center gap-2">
                  {Icon && <Icon size={13} className="text-maroon/30 flex-shrink-0" />}
                  <p className={`text-sm ${form[key as keyof typeof form] ? 'text-maroon' : 'text-maroon/30 italic'}`}>
                    {form[key as keyof typeof form] || 'Not set'}
                  </p>
                  {disabled && <span className="text-[10px] text-maroon/30">(cannot change)</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Ritual Preferences */}
      <div className="bg-white rounded-2xl border border-gold/15 overflow-hidden">
        <div className="px-5 py-4 border-b border-gold/10">
          <h3 className="text-sm font-semibold text-maroon flex items-center gap-2">🪔 Ritual Preferences</h3>
        </div>
        <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { label: 'Gotra', key: 'gotra' },
            { label: 'Nakshatra', key: 'nakshatra' },
          ].map(({ label, key }) => (
            <div key={key}>
              <label className="text-xs font-medium text-maroon/50 block mb-1">{label}</label>
              {editing ? (
                <input
                  type="text"
                  value={form[key as keyof typeof form]}
                  onChange={e => setForm(prev => ({ ...prev, [key]: e.target.value }))}
                  placeholder={`Enter your ${label.toLowerCase()}`}
                  className="w-full text-sm text-maroon border border-gold/20 rounded-xl px-3 py-2 outline-none focus:border-saffron/40 bg-cream/30 placeholder-maroon/25"
                />
              ) : (
                <p className={`text-sm ${form[key as keyof typeof form] ? 'text-maroon' : 'text-maroon/30 italic'}`}>
                  {form[key as keyof typeof form] || 'Not set'}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
